import React, { useMemo, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { listContenidoItems } from "../utils/contenidoOrden";
import {
  addTestRefAtPath,
  collectLinkedTestTitles,
  getNodeAtPath,
  insertChildAtPath,
  moveContenidoItemAtPath,
  moveNodeAtPath,
  pathKeyFromIndices,
  removeNodeAtPath,
  titlesAlongPath,
  updateNodeNameAtPath,
} from "../utils/temarioTree";
import { courseTestTitle, listLinkableCourseTests } from "../utils/listLinkableCourseTests";
import { displayArchivoNombre } from "../utils/archivoDisplayName";
import { getMessage } from "../data/locales";
import ViewportSelect from "./ViewportSelect";
import CourseDocumentFileActions from "./CourseDocumentFileActions";

function SortableRootRow({ id, className, style, disabled, children, testId }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });
  const mergedStyle = { ...style, transform: CSS.Transform.toString(transform), transition };
  return (
    <li ref={setNodeRef} style={mergedStyle} className={className} data-testid={testId}>
      <button
        type="button"
        className="temario-block-drag-handle"
        aria-label={getMessage("courseEditor.testsTemario.reorderBlock")}
        title={getMessage("courseEditor.testsTemario.reorderBlock")}
        disabled={disabled}
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      {children}
    </li>
  );
}

const DEPTH_HINT = ["Bloque", "Tema", "Unidad"];

function depthLabelForPath(path) {
  if (!Array.isArray(path) || path.length === 0) {
    return "Nivel";
  }
  const i = Math.min(path.length - 1, DEPTH_HINT.length - 1);
  return DEPTH_HINT[i];
}

function getArchivoListKey(archivo, index) {
  const keyParts = [
    archivo?.id,
    archivo?._id,
    archivo?.storageKey,
    archivo?.url,
    archivo?.nombre,
    archivo?.filename,
    displayArchivoNombre(archivo),
  ].filter((part) => typeof part === "string" && part.trim() !== "");
  const baseKey = keyParts.length > 0 ? keyParts.join(":") : JSON.stringify(archivo);
  return `${baseKey || "archivo"}:${index}`;
}

function contenidoItemLabel(item) {
  if (item?.kind === "test") {
    const titulo = typeof item.testRef?.titulo === "string" ? item.testRef.titulo : "";
    return getMessage("topic.testResourceLabel", { title: titulo });
  }
  return displayArchivoNombre(item?.archivo);
}

function contenidoItemMoveName(item) {
  if (item?.kind === "test") {
    return typeof item.testRef?.titulo === "string" ? item.testRef.titulo : "";
  }
  return displayArchivoNombre(item?.archivo);
}

function TemarioResourceRow({
  item,
  itemIdx,
  itemCount,
  disabled,
  path,
  rootNodes,
  onNodesChange,
  onArchivoOpen,
  onArchivoDownload,
  onArchivoRemove,
}) {
  const isTest = item.kind === "test";
  const label = contenidoItemLabel(item);
  const moveName = contenidoItemMoveName(item);
  const upKey = isTest ? "courseEditor.testsTemario.moveTestUp" : "courseEditor.testsTemario.moveFileUp";
  const downKey = isTest ? "courseEditor.testsTemario.moveTestDown" : "courseEditor.testsTemario.moveFileDown";
  const upLabelKey = isTest ? "courseEditor.testsTemario.moveTestUpLabel" : "courseEditor.testsTemario.moveFileUpLabel";
  const downLabelKey = isTest
    ? "courseEditor.testsTemario.moveTestDownLabel"
    : "courseEditor.testsTemario.moveFileDownLabel";

  const moveBy = (delta) => {
    const next = moveContenidoItemAtPath(rootNodes, path, item.kind, item.sourceIndex, delta);
    if (next) {
      onNodesChange(next);
    }
  };

  return (
    <li
      className={isTest
        ? "temario-content-resource-row temario-content-resource-row--test"
        : "temario-content-resource-row"}
    >
      <span className={isTest
        ? "temario-content-resource-name temario-content-resource-name--test"
        : "temario-content-resource-name"}
      >
        {label}
      </span>
      <button
        type="button"
        disabled={disabled || itemIdx === 0}
        aria-label={getMessage(upKey, { name: moveName })}
        title={getMessage(upKey, { name: moveName })}
        onClick={() => moveBy(-1)}
      >
        {getMessage(upLabelKey)}
      </button>
      <button
        type="button"
        disabled={disabled || itemIdx >= itemCount - 1}
        aria-label={getMessage(downKey, { name: moveName })}
        title={getMessage(downKey, { name: moveName })}
        onClick={() => moveBy(1)}
      >
        {getMessage(downLabelKey)}
      </button>
      {isTest ? null : (
        <>
          <CourseDocumentFileActions
            archivo={item.archivo}
            onOpen={onArchivoOpen}
            onDownload={onArchivoDownload}
          />
          {typeof onArchivoRemove === "function" && (
            <button
              type="button"
              className="style2-button"
              disabled={disabled}
              onClick={() => {
                if (!globalThis.confirm(`¿Quitar "${label}" del temario? (Podrás guardar después.)`)) {
                  return;
                }
                onArchivoRemove(path, item.sourceIndex);
              }}
            >
              Quitar del curso
            </button>
          )}
        </>
      )}
    </li>
  );
}

function ChildAddForm({ parentPath, disabled, rootNodes, onNodesChange }) {
  const [value, setValue] = useState("");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap" }}>
      <input
        className="form-control"
        style={{ maxWidth: 280 }}
        placeholder={`Nuevo ${(DEPTH_HINT[Math.min(parentPath.length, DEPTH_HINT.length - 1)] || "elemento").toLowerCase()}`}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
      />
      <button
        type="button"
        className="style2-button"
        disabled={disabled}
        onClick={() => {
          const trimmed = value.trim();
          if (!trimmed) {
            return;
          }
          const next = insertChildAtPath(rootNodes, parentPath, trimmed);
          if (next) {
            onNodesChange(next);
            setValue("");
          }
        }}
      >
        Añadir hijo
      </button>
    </div>
  );
}

function TemarioSubtree({
  rootNodes,
  path,
  disabled,
  onNodesChange,
  onArchivoOpen,
  onArchivoDownload,
  onArchivoRemove,
  availableTests,
  selectedTabLabel,
}) {
  const n = getNodeAtPath(rootNodes, path);
  if (!n) {
    return null;
  }

  const breadcrumb = titlesAlongPath(rootNodes, path).join(" / ");
  const label = depthLabelForPath(path);
  const items = listContenidoItems(n);
  const linkedTitles = collectLinkedTestTitles(rootNodes);
  const linkableTests = (availableTests || []).filter((test) => !linkedTitles.has(courseTestTitle(test)));

  return (
    <div
      style={{
        marginTop: 8,
        marginLeft: path.length > 1 ? 14 : 0,
        paddingLeft: path.length > 1 ? 10 : 0,
        borderLeft: path.length > 1 ? "1px solid rgba(23,163,152,0.35)" : "none",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
        <span className="temario-editor-node-label">
          {label}
          {breadcrumb ? `: ${breadcrumb}` : ""}
        </span>
        <input
          className="form-control medio-ancho"
          style={{ minWidth: 160 }}
          value={n.nombre}
          disabled={disabled}
          onChange={(e) => {
            const next = updateNodeNameAtPath(rootNodes, path, e.target.value ?? "");
            if (next) {
              onNodesChange(next);
            }
          }}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            const next = moveNodeAtPath(rootNodes, path, -1);
            if (next) {
              onNodesChange(next);
            }
          }}
        >
          ↑
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            const next = moveNodeAtPath(rootNodes, path, 1);
            if (next) {
              onNodesChange(next);
            }
          }}
        >
          ↓
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            const title = (n.nombre || "").trim() || "este elemento";
            if (!globalThis.confirm(`¿Eliminar "${title}" y todo lo que cuelga de él?`)) {
              return;
            }
            const next = removeNodeAtPath(rootNodes, path);
            if (next) {
              onNodesChange(next);
            }
          }}
        >
          Eliminar
        </button>
      </div>
      {items.length > 0 && (
        <ul className="temario-content-resource-list">
          {items.map((item, itemIdx) => (
            <TemarioResourceRow
              key={item.kind === "test"
                ? `test:${item.testRef?.titulo || "test"}:${item.sourceIndex}`
                : getArchivoListKey(item.archivo, item.sourceIndex)}
              item={item}
              itemIdx={itemIdx}
              itemCount={items.length}
              disabled={disabled}
              path={path}
              rootNodes={rootNodes}
              onNodesChange={onNodesChange}
              onArchivoOpen={onArchivoOpen}
              onArchivoDownload={onArchivoDownload}
              onArchivoRemove={onArchivoRemove}
            />
          ))}
        </ul>
      )}
      <div className="temario-editor-link-test" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
        <p className="temario-editor-link-test-heading">
          {getMessage("courseEditor.testsTemario.editorLinkHeading")}
        </p>
        <label className="temario-editor-link-test-label" htmlFor={`temario-link-test-${pathKeyFromIndices(path)}`}>
          {getMessage("courseEditor.testsTemario.editorLinkLabel")}
        </label>
        {availableTests.length === 0 ? (
          <p className="temario-editor-link-test-empty">{getMessage("courseEditor.testsTemario.editorNoTests")}</p>
        ) : linkableTests.length === 0 ? (
          <p className="temario-editor-link-test-empty">{getMessage("courseEditor.testsTemario.editorAllLinked")}</p>
        ) : (
          <ViewportSelect
            id={`temario-link-test-${pathKeyFromIndices(path)}`}
            className="form-control"
            style={{ maxWidth: 280 }}
            value=""
            disabled={disabled}
            onChange={(e) => {
              const titulo = e.target.value;
              if (!titulo) {
                return;
              }
              const next = addTestRefAtPath(rootNodes, path, {
                titulo,
                bloque: selectedTabLabel || "",
              });
              if (next) {
                onNodesChange(next);
              }
            }}
          >
            <option value="">{getMessage("courseEditor.testsTemario.editorLinkLabel")}…</option>
            {linkableTests.map((test) => {
              const titulo = courseTestTitle(test);
              return (
                <option key={titulo} value={titulo}>
                  {titulo}
                </option>
              );
            })}
          </ViewportSelect>
        )}
      </div>
      <ChildAddForm
        parentPath={path}
        disabled={disabled}
        rootNodes={rootNodes}
        onNodesChange={onNodesChange}
      />
      {(n.hijos || []).map((_child, j) => (
        <TemarioSubtree
          key={`${pathKeyFromIndices([...path, j])}`}
          rootNodes={rootNodes}
          path={[...path, j]}
          disabled={disabled}
          onNodesChange={onNodesChange}
          onArchivoOpen={onArchivoOpen}
          onArchivoDownload={onArchivoDownload}
          onArchivoRemove={onArchivoRemove}
          availableTests={availableTests}
          selectedTabLabel={selectedTabLabel}
        />
      ))}
    </div>
  );
}

function getRootBlockId(node, idx) {
  return `blk:${idx}:${(node?.nombre ?? "").trim()}`;
}

/**
 * @param {object} props
 * @param {Array<{ nombre: string, archivos: unknown[], hijos: unknown[] }>} props.nodes
 * @param {(next: typeof props.nodes) => void} props.onNodesChange
 * @param {boolean} props.disabled
 * @param {string} props.selectedTabLabel
 * @param {string} [props.newRootPlaceholder]
 * @param {(archivo: { nombre: string, tipo?: string, url?: string, storageKey?: string }) => void} [props.onArchivoOpen]
 * @param {(archivo: { nombre: string, tipo?: string, url?: string, storageKey?: string }) => void} [props.onArchivoDownload]
 * @param {(path: number[], archivoIndex: number) => void} [props.onArchivoRemove]
 * @param {object[]} [props.courseTests]
 */
export default function TemarioContenidoEditor({
  nodes,
  onNodesChange,
  disabled,
  selectedTabLabel,
  newRootPlaceholder,
  onArchivoOpen,
  onArchivoDownload,
  onArchivoRemove,
  courseTests,
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const list = useMemo(() => (Array.isArray(nodes) ? nodes : []), [nodes]);
  const availableTests = listLinkableCourseTests(courseTests, selectedTabLabel);
  const rootIds = list.map(getRootBlockId);
  const [nuevoNombreRaiz, setNuevoNombreRaiz] = useState("");

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = rootIds.indexOf(active.id);
    const newIndex = rootIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onNodesChange(arrayMove(list, oldIndex, newIndex));
  };

  return (
    <div className="temario-contenido-editor">
      <p className="temario-editor-help">
        {getMessage("courseEditor.testsTemario.docsTabHint")}
      </p>
      <p className="temario-editor-order-help">
        {getMessage("courseEditor.testsTemario.sharedOrderHint")}
      </p>
      {!selectedTabLabel ? (
        <p>Selecciona una pestaña para gestionar su temario.</p>
      ) : (
        <>
          <div className="form-group" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="form-control medio-ancho"
              placeholder={newRootPlaceholder || "Nombre del bloque en la raíz"}
              value={nuevoNombreRaiz}
              disabled={disabled}
              onChange={(e) => setNuevoNombreRaiz(e.target.value)}
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                const nombre = nuevoNombreRaiz.trim();
                if (!nombre) {
                  return;
                }
                const next = insertChildAtPath(list, [], nombre);
                if (next) {
                  onNodesChange(next);
                  setNuevoNombreRaiz("");
                }
              }}
            >
              Crear bloque en la raíz
            </button>
          </div>

          {list.length === 0 ? (
            <p style={{ marginTop: 12 }}>No hay nodos en esta pestaña. Crea un bloque o sube un documento con destino «Nuevo».</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={rootIds} strategy={verticalListSortingStrategy}>
                <ul className="temario-root-folder-list" style={{ listStyle: "none", padding: 0, marginTop: 12 }}>
                  {list.map((node, idx) => (
                    <SortableRootRow
                      key={getRootBlockId(node, idx)}
                      id={getRootBlockId(node, idx)}
                      testId={`temario-root-folder-${idx}`}
                      className="temario-sortable-root temario-root-folder"
                      style={{ marginBottom: 12, border: "1px solid rgba(23,163,152,0.25)", borderRadius: 8, padding: 10 }}
                      disabled={disabled || list.length <= 1}
                    >
                      <div className="temario-root-folder-header">
                        {`📁 ${typeof node?.nombre === "string" && node.nombre.trim() ? node.nombre.trim() : `Bloque ${idx + 1}`}`}
                      </div>
                      <TemarioSubtree
                        rootNodes={list}
                        path={[idx]}
                        disabled={disabled}
                        onNodesChange={onNodesChange}
                        onArchivoOpen={onArchivoOpen}
                        onArchivoDownload={onArchivoDownload}
                        onArchivoRemove={onArchivoRemove}
                        availableTests={availableTests}
                        selectedTabLabel={selectedTabLabel}
                      />
                    </SortableRootRow>
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </>
      )}
    </div>
  );
}
